import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Grid } from "@material-ui/core";
import httpClient from "../../lib/httpClient";
import { operationCancelled } from "../../lib/httpClient";

import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import { UpdateMapButton } from "../util/UpdateMapButton";
import {
  tableCellRender,
  stringFilterOperators,
  isSingleSelectOperators,
  filterModelToQueryValue,
  getCompanyCodeFilter,
  getCompanyCodeName,
  getLatestUpdateAgro,
} from "../util/tableUtils";
import { useClientConfig } from "../../context/clientConfigContext";

export const AgroAnalyticsPerilsList = ({
  filterModel,
  setFilterModel,
  setFilteredMapData,
  wantedReferenceDateProp,
}) => {
  const { t } = useTranslation();
  const config = useClientConfig();

  const [sortModel, setSortModel] = useState({});
  const [policiesData, setPoliciesData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [tableState, setTableState] = useState({
    isLoading: false,
    total: 0,
  });

  const [lastUpdateDatePoliciesMsg, setLastUpdateDatePoliciesMsg] = useState("");
  const policiesCancelTokenRef = useRef(null);

  useEffect(() => {
    getLatestUpdateAgro("/serviceLogs/agriculture?service=agricultureDb_contratti").then((lastUpdateMsg) => {
      setLastUpdateDatePoliciesMsg(lastUpdateMsg);
    });
  }, []);

  const getPoliciesData = useCallback(async () => {
    let sortField, sortDirection;
    const sortItem = sortModel;
    if (sortItem && Object.keys(sortItem).length) {
      console.log("sortItem", sortItem);
      const { field, sort } = sortItem[0];
      console.log("field", field);
      console.log("sort", sort);
      sortField = field;
      sortDirection = sort;
    }

    const load = {
      aggr: "main",
      refDate: wantedReferenceDateProp,
      page: paginationModel.page,
      pagesize: paginationModel.pageSize,
      filters: filterModelToQueryValue(filterModel),
      orderBy: sortField,
      orderDirection: sortDirection,
    };

    try {
      setTableState((old) => ({ ...old, isLoading: true }));

      const { data: policiesResponse } = await httpClient().post("proxy/agroanalytics_reale", load, {
        cancelToken: policiesCancelTokenRef.current.token,
      });

      setPoliciesData(policiesResponse.data?.map((pol) => ({ ...pol, selected: false })));
      setTableState((old) => ({
        ...old,
        isLoading: false,
        total: policiesResponse.total,
      }));
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Request canceled", error.message);
      } else if (error.code === "ECONNABORTED") {
        console.error("Request timed out");
      } else {
        alert(
          error.message +
            ". You encountered an internal error, please try again later. We cannot access the backend server when loading policies data," +
            "  error code=" +
            error.code +
            "  URL=" +
            httpClient().defaults.baseURL +
            "  request=" +
            JSON.stringify(load),
        );
        console.error("Error:", error);
      }
    }
  }, [filterModel, sortModel, paginationModel.page, paginationModel.pageSize, wantedReferenceDateProp]);

  useEffect(() => {
    const getPoliciesDataWrapper = async () => {
      // Cancel the previous request when the filterModel changes
      if (policiesCancelTokenRef.current) {
        policiesCancelTokenRef.current.cancel(operationCancelled);
      }

      // Create a new cancel token for the current request
      policiesCancelTokenRef.current = axios.CancelToken.source();
      getPoliciesData();
    };

    if (filterModel) {
      const isAnyIncompleteFilter = filterModel?.items?.some((filter) => !filter?.value);

      if (!isAnyIncompleteFilter) {
        getPoliciesDataWrapper();
      }
    } else {
      getPoliciesDataWrapper();
    }
  }, [filterModel, getPoliciesData]);

  const onFilterChange = useCallback(
    (aFilterModel) => {
      setFilterModel(aFilterModel);
    },
    [setFilterModel],
  );

  const onSortChange = useCallback((aSortModel) => {
    setSortModel(aSortModel);
  }, []);

  const agroCompanyColumnFeatures = {
    width: 70,
    type: "singleSelect",
    filterOperators: isSingleSelectOperators,
    valueOptions: getCompanyCodeFilter(config.agriCompanyCodesMap),
    valueGetter: (params) => getCompanyCodeName(params.row.Compagnia, config.agriCompanyCodesMap),
  };

  const policiesColumns = [
    {
      field: "IDContratto",
      headerName: t("agriculture.policyNo"),
      width: 70,
    },

    {
      headerName: t("agriculture.year"),
      width: 60,
      field: "Anno",
      type: "singleSelect",
      filterable: false,
      filterOperators: isSingleSelectOperators,
      // valueOptions: ["2017", "2018", "2019", "2020", "2021", "2022", "2023", "2024"],
    },

    {
      field: "Compagnia",
      headerName: t("company"),
      ...agroCompanyColumnFeatures,
    },

    {
      field: "DescrizioneCampagna",
      headerName: t("agriculture.season"),
      width: 100,
    },

    {
      field: "CodiceAgenzia",
      headerName: t("agencyCode"),
    },

    {
      field: "DescrizioneAgenzia",
      headerName: t("agency"),
      width: 180,
    },
    {
      field: "DescrizioneGaranzia",
      headerName: t("agriculture.perilName"),
      width: 200,
    },

    {
      field: "Specie",
      headerName: t("agriculture.specie"),
      width: 200,
    },
    {
      field: "CodiceIstat",
      headerName: t("cityCode"),
      width: 70,
    },

    {
      field: "Comune",
      headerName: t("city"),
      width: 180,
    },

    {
      field: "Provincia",
      headerName: t("state"),
      width: 20,
    },
  ];

  const columns = policiesColumns.map((tableColumn) => ({
    valueFormatter: (params) => tableCellRender(params.value?.toString()),
    filterOperators: stringFilterOperators,
    ...tableColumn,
  }));

  return (
    <Grid item xs={12} sm={12}>
      <div style={{ width: "100%" }}>
        <DataGridCystellarTable
          density={"compact"}
          pageSizeOptions={[5, 25, 50, 100]}
          columns={columns}
          rows={policiesData}
          rowCount={tableState.total}
          getRowId={(rowData) => rowData._id}
          loading={tableState.isLoading}
          paginationMode={"server"}
          paginationModel={paginationModel}
          onPaginationModelChange={(newPaginationModel) => setPaginationModel(newPaginationModel)}
          filterMode={"server"}
          onFilterModelChange={onFilterChange}
          sortingMode={"server"}
          onSortModelChange={onSortChange}
          customToolbarComp={true}
          customToolbarCompMsg={lastUpdateDatePoliciesMsg}
          customToolbarWidgets={
            <UpdateMapButton
              setFilteredMapData={setFilteredMapData}
              filterModel={filterModel}
              routeName="policies"
              variant="AgroClimateAnalytics"
              wantedReferenceDate={wantedReferenceDateProp}
            />
          }
        />
      </div>
    </Grid>
  );
};
